import type { Active } from '@dnd-kit/core';
import React from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { selectCurrentTrip } from '../../../redux/trip/trip.selectors';
import type { TripCard, TripEvent } from '../../../redux/trip/trip.types';
import { ItemType } from '../../../types/DragAndDrop';
import { getOpenStatus } from '../../../utils/Card';
import { textColorForBg } from '../../../utils/color.utils';
import { getEmojiAndColorForCard } from '../../../utils/getEmojiAndColorForCard';
import { getCachedPlacePhotoUrl } from '../../../utils/photo-cache.utils';
import { getNotesPreviewText, isNotesEmpty } from '../../../utils/slate.utils';

// Max characters shown for event title in the drag ghost before truncating
const EVENT_GHOST_NAME_MAX = 70;

interface DragOverlayContentProps {
  activeItem: Active | null;
}

/**
 * Renders the drag ghost element displayed inside `<DragOverlay>` during a drag.
 * Each draggable type gets a distinct visual treatment:
 *
 * - CARD    → thumbnail image + card name pill
 * - EVENT   → full itinerary-event clone (color, emoji, notes preview, open status)
 * - LIST    → teal-bordered pill with emoji + list name
 * - DIVIDER → blue-bordered pill with chevron + emoji + section name
 */
function DragOverlayContent({ activeItem }: DragOverlayContentProps): React.ReactElement | null {
  const currentTrip = useAppSelector(selectCurrentTrip);

  if (!activeItem || !currentTrip) {
    return null;
  }

  const type = activeItem.data.current?.type as string | undefined;

  // ── CARD ─────────────────────────────────────────────────────────────────
  if (type === ItemType.CARD) {
    const card = activeItem.data.current?.card as TripCard | undefined;
    const name = card?.customName ?? card?.name ?? '';
    // Drag ghost is ephemeral and re-renders on every pointer move, so this reads
    // whatever's already cached from the card's own render rather than resolving
    // (and potentially fetching) a photo mid-drag.
    const defaultImageIndex = card?.defaultImage ?? 0;
    const imgUrl =
      getCachedPlacePhotoUrl(card?.photoReferences?.[defaultImageIndex]) ??
      (card?.imgUrls && card.imgUrls.length > 0 ? card.imgUrls[defaultImageIndex] : undefined);
    return (
      <div className='drag-ghost drag-ghost--card'>
        <div
          className='drag-ghost__card-img'
          style={imgUrl ? { backgroundImage: `url(${imgUrl})` } : undefined}
        />
        <span className='drag-ghost__card-name'>{name}</span>
      </div>
    );
  }

  // ── EVENT ─────────────────────────────────────────────────────────────────
  if (type === ItemType.EVENT) {
    const card = activeItem.data.current?.card as TripCard | undefined;
    const event = activeItem.data.current?.event as TripEvent | undefined;
    if (!card || !event) {
      return null;
    }

    const { emoji, color, listLabel } = getEmojiAndColorForCard(
      card.id,
      currentTrip.lists,
      currentTrip.dividers ?? []
    );
    const textColor = textColorForBg(color);
    const displayName = card.customName ?? card.name ?? '';
    const truncatedName =
      displayName.length > EVENT_GHOST_NAME_MAX
        ? `${displayName.substring(0, EVENT_GHOST_NAME_MAX)}...`
        : displayName;
    const notesPreview = !isNotesEmpty(card) && card.notes ? getNotesPreviewText(card.notes) : null;
    const openStatus = getOpenStatus(card.openingHours);

    return (
      <div
        className={`itinerary-event-container ${event.isConfirmed ? 'confirmed' : 'unconfirmed'}`}
        style={{ background: color }}
      >
        <span
          className='itinerary-event-icon'
          title={listLabel}
        >
          {emoji}
        </span>
        <div
          className='itinerary-event-body'
          style={{ color: textColor }}
        >
          <span className='itinerary-event-title'>{truncatedName}</span>
          {notesPreview && <p className='itinerary-event-notes-preview'>{notesPreview}</p>}
          {openStatus && (
            <span className='itinerary-event-open-status'>
              {openStatus.isOpen ? (
                openStatus.label
              ) : (
                <>
                  <span className='itinerary-event-open-status__closed'>Closed</span>
                  {openStatus.nextOpenTime && ` · Opens ${openStatus.nextOpenTime}`}
                </>
              )}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── LIST ──────────────────────────────────────────────────────────────────
  if (type === ItemType.LIST) {
    const list = currentTrip.lists.find((l) => l.id === activeItem.id);
    return (
      <div className='drag-ghost drag-ghost--list'>
        {list?.emoji && <span className='drag-ghost__emoji'>{list.emoji}</span>}
        <span className='drag-ghost__name'>{list?.name ?? ''}</span>
      </div>
    );
  }

  // ── DIVIDER ───────────────────────────────────────────────────────────────
  if (type === ItemType.DIVIDER) {
    const divider = currentTrip.dividers?.find((d) => d.id === activeItem.id);
    return (
      <div className='drag-ghost drag-ghost--divider'>
        <span className='drag-ghost__chevron'>›</span>
        {divider?.emoji && <span className='drag-ghost__emoji'>{divider.emoji}</span>}
        <span className='drag-ghost__name'>{divider?.name ?? 'Section'}</span>
      </div>
    );
  }

  return null;
}

DragOverlayContent.displayName = 'DragOverlayContent';
export default DragOverlayContent;
